/*
 * Copyright (c) 2024-2025 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { ExtensionOrganizationResponse } from '@cp/customer-database-sdk'
import { BaseUserHelpers } from '../BaseUserHelpers'

import { DEFAULT_RENDER_FUNCTION, getColumnValue } from './TableColumnHelpers'
import { ColumnDefinitionFn, ColumnRendererRecord, ValidOrganizationColumn } from './types'

const COLUMN_TITLE_MAPPING = {
  name: 'Organization Name',
  'finance.hostingAndInfraBillingModel': 'Hosting and Infra Billing Model',
  'location.region': 'Region',
  retentionStatus: 'Status',
  timezone: 'Timezone',
  accountManager: 'Account Manager',
  'executiveSponsor.primary': 'Primary ES',
  'executiveSponsor.secondary': 'Secondary ES',
  isStarter: 'Registration Channel',
  productsActive: 'Products Active',
  'solutionArchitect.primary': 'Primary Solution Architect',
  'solutionArchitect.secondary': 'Supporting Solution Architect',
  'organizationPriority.gameTitles': 'Game Titles',
  'organizationPriority.supportTier': 'Support Tier'
} satisfies Record<ValidOrganizationColumn, string>

const DEFAULT_COLUMN_RENDERER_MAPPING = {
  name: undefined,
  accountManager: accountManager => BaseUserHelpers.renderFullName(accountManager),
  'executiveSponsor.primary': primaryES => BaseUserHelpers.renderFullName(primaryES),
  'executiveSponsor.secondary': secondaryES => BaseUserHelpers.renderFullName(secondaryES),
  isStarter: isStarter => (isStarter ? 'Shared Cloud' : 'HubSpot or others'),
  'solutionArchitect.primary': primarySA => BaseUserHelpers.renderFullName(primarySA),
  'solutionArchitect.secondary': secondarySA => BaseUserHelpers.renderFullName(secondarySA),
  'organizationPriority.gameTitles': gameTitles => (gameTitles.length > 0 ? gameTitles.join(', ') : '-'),
  'organizationPriority.supportTier': supportTier => supportTier || '-',
  'finance.hostingAndInfraBillingModel': (_, organization) => {
    const { isTransitioningFromHubSpot, hubSpotId, isStarter, finance } = organization
    if (isTransitioningFromHubSpot && hubSpotId && !isStarter) {
      return finance.hostingAndInfraBillingModel || '-'
    }

    return isStarter ? 'Shared Cloud' : 'Private Cloud'
  },
  'location.region': region => region || '-',
  productsActive: products => (products.length > 0 ? products.join(', ') : '-'),
  retentionStatus: status => status || '-',
  timezone: timezone => timezone || '-'
} satisfies ColumnRendererRecord<ExtensionOrganizationResponse, ValidOrganizationColumn>

const DEFAULT_COLUMNS_RECORD = [
  'name',
  'organizationPriority.supportTier',
  'accountManager',
  'solutionArchitect.primary',
  'solutionArchitect.secondary',
  'location.region',
  'finance.hostingAndInfraBillingModel',
  'productsActive',
  'organizationPriority.gameTitles',
  'retentionStatus'
] satisfies Array<ValidOrganizationColumn>

export class OrganizationTableColumnDefinitionHelpers {
  static VALID_ORGANIZATION_COLUMNS = Object.keys(DEFAULT_COLUMN_RENDERER_MAPPING) as Array<ValidOrganizationColumn>
  static DEFAULT_COLUMNS_RECORD = DEFAULT_COLUMNS_RECORD

  static getDefaultOrganizationColumnsTitleMapping = (columnsKey: Array<ValidOrganizationColumn> | undefined = DEFAULT_COLUMNS_RECORD) => {
    const columnTitle: string[] = []
    for (const key of columnsKey) {
      const mappedColumnTitleFromKey = COLUMN_TITLE_MAPPING[key]
      if (mappedColumnTitleFromKey) {
        columnTitle.push(mappedColumnTitleFromKey)
      }
    }

    return columnTitle
  }

  static getOrganizationColumn: ColumnDefinitionFn<ExtensionOrganizationResponse, ValidOrganizationColumn> = (param, renderFn) => ({
    name: param,
    title: COLUMN_TITLE_MAPPING[param],
    render: record => {
      const effectiveRenderFn = renderFn ?? (DEFAULT_COLUMN_RENDERER_MAPPING[param] as typeof renderFn) ?? DEFAULT_RENDER_FUNCTION
      const effectiveValue = getColumnValue(record, param)
      return effectiveRenderFn(effectiveValue, record)
    }
  })
}
